import camelCaseKeys from "camelcase-keys";
import { useSelector } from "react-redux";
import { RivalsAppProps, RivalsPageProps } from "rivals/Context";
import TransferTracker from "rivals/components/pages/TransferTracker";
import {
  LIST_FILTERS_PARAMS,
  PEOPLE_FILTERS_TYPES
} from "rivals/components/pages/TransferTracker/types";
import ErrorPage from "rivals/components/pages/error/ErrorPage";
import { fetchListData, getContentListPeople } from "rivals/redux/list/actions";
import {
  getError,
  getList,
  getListVersions,
  getValidFilters
} from "rivals/redux/list/selectors";
import { getPositions } from "rivals/redux/position/actions";
import { getRailsConstants } from "rivals/redux/rails/actions";
import { RailsConstantsKeys } from "rivals/redux/rails/reducer";
import { State } from "rivals/redux/reducer";
import { getTransfers } from "rivals/redux/transfer/actions";
import { RESPONSE_PAGE_NOT_FOUND_ERROR } from "rivals/services/base";
import {
  LAZY_LOADING_PAGE_SIZE,
  RIVALS_LIST_SPORTS
} from "rivals/shared/constants";
import { RequestParams } from "rivals/shared/interfaces/List";
import { PositionType } from "rivals/shared/interfaces/Position";
import { ListKinds } from "rivals/shared/interfaces/adminLists";
import styles from "./TransferTracker.module.scss";

interface Props extends RivalsPageProps {
  kind: string;
  schoolFocus?: string;
  sport: string;
  year: number;
}

function TransferTrackerPage({
  schoolFocus,
  sport,
  statusCode,
  year
}: Props): JSX.Element {
  const error = useSelector(getError);
  const list = useSelector(getList);
  const versions = useSelector(getListVersions);
  // Pass in/display only filters that have been validated from the backend
  const validFilters = useSelector(getValidFilters);

  if (
    statusCode === RESPONSE_PAGE_NOT_FOUND_ERROR ||
    // redundant because of handleRedirect below but fixes type issues within TransferTracker
    !list ||
    !versions ||
    error
  ) {
    return <ErrorPage />;
  }

  return (
    <main className={styles.wrapper} role="main">
      <TransferTracker
        list={list}
        schoolFocus={schoolFocus}
        sport={sport}
        urlFilters={validFilters}
        versions={versions}
        year={year}
      />
    </main>
  );
}

// Custom logic to check for required page data and redirect with 404 if not present
TransferTrackerPage.handleRedirect = (state: State): boolean => {
  return !getList(state) || !getListVersions(state) || !!getError(state);
};

TransferTrackerPage.getInitialProps = async ({
  isServer,
  query,
  res,
  store: { dispatch }
}: RivalsAppProps): Promise<Props> => {
  // config regex supports either 1 or 2 args in the slug
  const year = parseInt(query.slug[1] || query.slug[0]);
  const kind = ListKinds.TRANSFER_PORTAL;
  const sport =
    query.slug.length > 1
      ? query.slug[0]
      : query?.sport?.toString() || RIVALS_LIST_SPORTS.FOOTBALL;
  const version = query?.version as string | undefined;

  // school_focus will filter both latest activity widget and top transfers table.
  const schoolFocus = query[LIST_FILTERS_PARAMS.SCHOOL_FOCUS]
    ?.toString()
    ?.toLowerCase();

  // latest_activity_college_name will only filter latest activity widget
  const latestActivityCollege = query[
    LIST_FILTERS_PARAMS.LATEST_ACTIVITY_COLLEGE
  ]
    ?.toString()
    ?.toLowerCase();

  const listPeopleParams: RequestParams = {
    college: schoolFocus || "",
    kind,
    size: LAZY_LOADING_PAGE_SIZE,
    sport,
    start: 1,
    version,
    year
  };

  const queryParams = camelCaseKeys(query);
  const filterOptions = Object.values(PEOPLE_FILTERS_TYPES);
  Object.keys(queryParams).forEach(key => {
    const value = queryParams[key as keyof Object].toString().toLowerCase();
    const isValidParam = filterOptions.includes(key as PEOPLE_FILTERS_TYPES);
    // Get params from url and set values as filters and listPeopleParams for api call
    if (value && isValidParam) {
      listPeopleParams[key as PEOPLE_FILTERS_TYPES] = value;
    }
  });

  dispatch(getRailsConstants([RailsConstantsKeys.ADS_SETTINGS]));
  dispatch(fetchListData({ kind, sport, version, year }));
  dispatch(getContentListPeople(listPeopleParams, true));

  await dispatch(
    getTransfers({
      college: schoolFocus || latestActivityCollege || "",
      isWidget: true,
      sport
    })
  );
  await dispatch(
    getPositions({ sport: sport, type: PositionType.collegePlayer, year: year })
  );

  return {
    isServer,
    kind,
    schoolFocus,
    sport,
    statusCode: res?.statusCode,
    year
  };
};

export default TransferTrackerPage;
